/** Shared constants and helpers for the Funnel Builder UI. */

export const FUNNEL_WRITE_ROLES = ['manager', 'admin', 'super_admin'];
export const FUNNEL_ADMIN_ROLES = ['admin', 'super_admin'];

export const BLOCK_TYPES = [
  { value: 'hero', label: 'Hero', description: 'Headline banner with logo and colors', icon: 'layout' },
  { value: 'vsl', label: 'Video (VSL)', description: 'Video sales letter embed', icon: 'video' },
  { value: 'cta', label: 'Call to Action', description: 'Button linking to next step or URL', icon: 'mouse-pointer' },
  { value: 'faq', label: 'FAQ', description: 'Question and answer accordion', icon: 'help-circle' },
  { value: 'testimonials', label: 'Testimonials', description: 'Social proof from agents', icon: 'message-square' },
  { value: 'steps', label: 'Process Steps', description: 'Numbered how-it-works list', icon: 'list' },
  { value: 'booking', label: 'Booking', description: 'Calendar appointment widget', icon: 'calendar' },
  { value: 'author', label: 'Author / Agent', description: 'Showcase an agent profile with photo and credentials', icon: 'user' },
];

export const STEP_TYPES = [
  { value: 'optin', label: 'Opt-in' },
  { value: 'watch', label: 'Watch' },
  { value: 'book', label: 'Book' },
  { value: 'custom', label: 'Custom' },
];

export const FUNNEL_STATUS_OPTIONS = [
  { value: 'draft', label: 'Draft' },
  { value: 'active', label: 'Active' },
  { value: 'paused', label: 'Paused' },
];

export const SEQUENCE_TRIGGER_OPTIONS = [
  { value: 'optin', label: 'Opt-in', hint: 'Enroll when a visitor submits the opt-in form.' },
  { value: 'watch', label: 'Watch', hint: 'Enroll when a visitor watches the VSL (future tracking).' },
  { value: 'book', label: 'Book', hint: 'Enroll when a visitor books an appointment.' },
  { value: 'any', label: 'Any', hint: 'Enroll on any submission from this funnel.' },
];

export function slugify(text) {
  return String(text || '')
    .toLowerCase()
    .trim()
    .replace(/[^\w\s-]/g, '')
    .replace(/[\s_-]+/g, '-')
    .replace(/^-+|-+$/g, '');
}

export function getFunnelLiveUrl(slug, stepSlug = null) {
  if (!slug) return null;
  const path = stepSlug ? `/${slug}/${stepSlug}` : `/${slug}`;
  return `https://go.prosperityleaders.net${path}`;
}

export function getDefaultBlockContent(blockType) {
  switch (blockType) {
    case 'hero':
      return {
        headline: 'Build a Six-Figure Income Helping Families',
        subheadline: 'Join Prosperity Leaders and change lives while you build wealth.',
        background_color: '#0F1923',
        text_color: '#ffffff',
        logo_url: '',
      };
    case 'vsl':
      return {
        video_url: '',
        autoplay: false,
        thumbnail_url: '',
        caption: 'Watch this short presentation to see how our agents succeed.',
      };
    case 'cta':
      return {
        button_text: 'Continue',
        use_booking: false,
        url: '',
        target_step: null,
        background_color: '#00AAFF',
        text_color: '#ffffff',
      };
    case 'faq':
      return {
        items: [
          {
            question: 'Do I need sales experience?',
            answer: 'No prior experience required. We provide full training, scripts, and mentorship.',
          },
          {
            question: 'How much can I earn?',
            answer: 'Top agents earn six figures in their first year with our proven system and lead flow.',
          },
        ],
      };
    case 'testimonials':
      return {
        items: [
          {
            name: 'Marcus T.',
            result: '$127K first year',
            detail: 'I left my 9-to-5 and never looked back. Prosperity Leaders gave me the playbook.',
          },
          {
            name: 'Jennifer R.',
            result: 'Promoted to Manager in 8 months',
            detail: 'The training and support here is unlike anything I experienced in corporate.',
          },
        ],
      };
    case 'steps':
      return {
        headline: 'How It Works',
        items: [
          { number: 1, title: 'Apply', description: 'Submit your info and schedule a brief intro call.' },
          { number: 2, title: 'Train', description: 'Complete our agent onboarding and licensing support.' },
          { number: 3, title: 'Launch', description: 'Start taking appointments and earning commissions.' },
        ],
      };
    case 'booking':
      return {
        title: 'Book Your Strategy Call',
        subtitle: 'Pick a time that works for you — no obligation.',
        assignment_mode: 'round_robin',
        assigned_user_id: null,
      };
    case 'author':
      return {
        agent_id: null,
        visible_fields: {
          headshot: true,
          name: true,
          title: true,
          bio: true,
          preferred_quote: false,
          best_income_milestone: true,
          years_in_industry: false,
          clients_served: false,
          designations: true,
          awards: false,
          skills: false,
          facebook_url: false,
          instagram_url: false,
          linkedin_url: false,
          youtube_url: false,
          tiktok_url: false,
          personal_cta_text: true,
          personal_booking_url: false,
        },
        layout: 'card',
      };
    default:
      return {};
  }
}

/** Default blocks placed on each step type when starting from scratch. */
export function getDefaultBlocksForStepType(stepType) {
  const map = {
    optin: ['hero', 'cta'],
    watch: ['vsl', 'testimonials', 'cta'],
    book: ['steps', 'booking', 'faq'],
    custom: ['hero'],
  };
  return (map[stepType] || ['hero']).map((blockType, i) => ({
    block_type: blockType,
    position: i,
    content: getDefaultBlockContent(blockType),
    is_visible: true,
  }));
}

/** Three default steps for "start from scratch". */
export function getScratchSteps() {
  return [
    { step_number: 1, step_type: 'optin', title: 'Opt In', slug: 'optin', is_entry_point: true },
    { step_number: 2, step_type: 'watch', title: 'Watch Presentation', slug: 'watch', is_entry_point: false },
    { step_number: 3, step_type: 'book', title: 'Book a Call', slug: 'book', is_entry_point: false },
  ];
}

export function getStatusBadgeVariant(status) {
  if (status === 'active') return 'success';
  if (status === 'paused') return 'warning';
  return 'default';
}

export function summarizeBlockContent(block) {
  const c = block?.content || {};
  switch (block?.block_type) {
    case 'hero':
      return c.headline || 'Hero section';
    case 'vsl':
      return c.video_url ? 'Video configured' : 'No video URL yet';
    case 'cta':
      return c.button_text || 'Call to action';
    case 'faq':
      return `${(c.items || []).length} question(s)`;
    case 'testimonials':
      return `${(c.items || []).length} testimonial(s)`;
    case 'steps':
      return c.headline || `${(c.items || []).length} step(s)`;
    case 'booking':
      return c.title || 'Booking widget';
    case 'author':
      return c.agent_id ? 'Agent profile configured' : 'No agent selected';
    default:
      return block?.block_type || 'Block';
  }
}

/** System seed template definitions (used by MCP seed + FunnelNew). */
export const SYSTEM_FUNNEL_TEMPLATES = [
  {
    name: 'Salary Worker VSL',
    description: 'Full recruiting funnel: opt-in gate, VSL presentation, and booking page with all block types.',
    is_system: true,
    funnel_json: {
      steps: [
        {
          step_number: 1,
          step_type: 'optin',
          title: 'Get Instant Access',
          slug: 'optin',
          is_entry_point: true,
          blocks: [
            {
              block_type: 'hero',
              position: 0,
              content: {
                headline: 'Escape the Paycheck Trap — Build Real Wealth in Insurance',
                subheadline: 'Prosperity Leaders helps salary workers transition into a six-figure agency career with full training and mentorship.',
                background_color: '#0F1923',
                text_color: '#ffffff',
                logo_url: 'https://crm.prosperityleaders.net/logo.png',
              },
            },
            {
              block_type: 'cta',
              position: 1,
              content: {
                button_text: 'Watch the Free Presentation',
                use_booking: false,
                url: '',
                target_step: 2,
                background_color: '#00AAFF',
                text_color: '#ffffff',
              },
            },
          ],
        },
        {
          step_number: 2,
          step_type: 'watch',
          title: 'Watch the Presentation',
          slug: 'watch',
          is_entry_point: false,
          blocks: [
            {
              block_type: 'vsl',
              position: 0,
              content: {
                video_url: 'https://www.youtube.com/watch?v=dQw4w9WgXcQ',
                autoplay: false,
                thumbnail_url: '',
                caption: 'See how everyday professionals are replacing their salary with unlimited commission income.',
              },
            },
            {
              block_type: 'testimonials',
              position: 1,
              content: {
                items: [
                  { name: 'David K.', result: 'Left $65K job → $140K year 1', detail: 'I was skeptical. Six months in I doubled my old salary.' },
                  { name: 'Alicia M.', result: 'Single mom, six figures', detail: 'Flexible schedule and real support from day one.' },
                ],
              },
            },
            {
              block_type: 'faq',
              position: 2,
              content: {
                items: [
                  { question: 'Is this MLM?', answer: 'No. We are a licensed life insurance agency. You sell real products with real commissions.' },
                  { question: 'Do I need a license?', answer: 'We guide you through licensing. Many agents get licensed within 2–4 weeks.' },
                ],
              },
            },
            {
              block_type: 'cta',
              position: 3,
              content: {
                button_text: 'Book My Strategy Call',
                use_booking: false,
                url: '',
                target_step: 3,
                background_color: '#00AAFF',
                text_color: '#ffffff',
              },
            },
          ],
        },
        {
          step_number: 3,
          step_type: 'book',
          title: 'Book Your Call',
          slug: 'book',
          is_entry_point: false,
          blocks: [
            {
              block_type: 'steps',
              position: 0,
              content: {
                headline: 'Your Path to Prosperity',
                items: [
                  { number: 1, title: 'Intro Call', description: '15-minute call to see if we are a mutual fit.' },
                  { number: 2, title: 'Onboarding', description: 'Licensing support and sales training.' },
                  { number: 3, title: 'Go Live', description: 'Start booking appointments and earning.' },
                ],
              },
            },
            {
              block_type: 'booking',
              position: 1,
              content: {
                title: 'Schedule Your Free Strategy Session',
                subtitle: 'Speak with a Prosperity Leaders recruiter — pick a time below.',
                assignment_mode: 'round_robin',
                assigned_user_id: null,
              },
            },
          ],
        },
      ],
    },
  },
  {
    name: 'Watch + Book',
    description: 'Two-step funnel: video presentation straight to booking — no opt-in gate.',
    is_system: true,
    funnel_json: {
      steps: [
        {
          step_number: 1,
          step_type: 'watch',
          title: 'Watch & Learn',
          slug: 'watch',
          is_entry_point: true,
          blocks: [
            {
              block_type: 'hero',
              position: 0,
              content: {
                headline: 'Discover the Prosperity Leaders Opportunity',
                subheadline: 'Watch this 12-minute presentation, then book your call.',
                background_color: '#0F1923',
                text_color: '#ffffff',
                logo_url: '',
              },
            },
            {
              block_type: 'vsl',
              position: 1,
              content: {
                video_url: '',
                autoplay: true,
                thumbnail_url: '',
                caption: 'Why top producers are joining Prosperity Leaders in 2026.',
              },
            },
            {
              block_type: 'cta',
              position: 2,
              content: {
                button_text: 'Book My Call Now',
                use_booking: false,
                url: '',
                target_step: 2,
                background_color: '#00AAFF',
                text_color: '#ffffff',
              },
            },
          ],
        },
        {
          step_number: 2,
          step_type: 'book',
          title: 'Book a Call',
          slug: 'book',
          is_entry_point: false,
          blocks: [
            {
              block_type: 'booking',
              position: 0,
              content: {
                title: 'Pick Your Time',
                subtitle: 'Choose a slot with our recruiting team.',
                assignment_mode: 'round_robin',
                assigned_user_id: null,
              },
            },
          ],
        },
      ],
    },
  },
  {
    name: 'Optin + Book',
    description: 'Capture lead info first, then go straight to booking — skip the VSL.',
    is_system: true,
    funnel_json: {
      steps: [
        {
          step_number: 1,
          step_type: 'optin',
          title: 'Get Started',
          slug: 'optin',
          is_entry_point: true,
          blocks: [
            {
              block_type: 'hero',
              position: 0,
              content: {
                headline: 'Ready to Change Your Financial Future?',
                subheadline: 'Enter your details and book a no-pressure intro call with Prosperity Leaders.',
                background_color: '#0F1923',
                text_color: '#ffffff',
                logo_url: '',
              },
            },
            {
              block_type: 'cta',
              position: 1,
              content: {
                button_text: 'Continue to Booking',
                use_booking: false,
                url: '',
                target_step: 2,
                background_color: '#00AAFF',
                text_color: '#ffffff',
              },
            },
          ],
        },
        {
          step_number: 2,
          step_type: 'book',
          title: 'Book Your Intro Call',
          slug: 'book',
          is_entry_point: false,
          blocks: [
            {
              block_type: 'steps',
              position: 0,
              content: {
                headline: 'What Happens Next',
                items: [
                  { number: 1, title: 'Book', description: 'Choose a time that works for you.' },
                  { number: 2, title: 'Connect', description: 'Speak with a recruiter about the opportunity.' },
                  { number: 3, title: 'Decide', description: 'No pressure — see if it is the right fit.' },
                ],
              },
            },
            {
              block_type: 'booking',
              position: 1,
              content: {
                title: 'Book Your Intro Call',
                subtitle: '15 minutes — learn how agents are building six-figure incomes.',
                assignment_mode: 'round_robin',
                assigned_user_id: null,
              },
            },
          ],
        },
      ],
    },
  },
];
